import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  RefreshCw,
  Calendar,
  Download,
  ChevronDown,
  ChevronRight,
  Users,
  XCircle,
  UserCheck,
  Database,
  TrendingUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.05 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

// Generate color palette for sales types
const getTypeColor = (index) => {
  const colors = [
    'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
    'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
    'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
    'bg-pink-100 text-pink-800 dark:bg-pink-900/40 dark:text-pink-300',
    'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300',
    'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300',
    'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
    'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300',
  ];
  return colors[index % colors.length];
};

const getTypeHeaderColor = (index) => {
  const colors = [
    'bg-green-600 text-white',
    'bg-blue-600 text-white',
    'bg-purple-600 text-white',
    'bg-amber-600 text-white',
    'bg-emerald-600 text-white',
    'bg-pink-600 text-white',
    'bg-cyan-600 text-white',
    'bg-indigo-600 text-white',
    'bg-orange-600 text-white',
    'bg-teal-600 text-white',
  ];
  return colors[index % colors.length];
};

export default function MonthlyDataSales() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedPersonnel, setExpandedPersonnel] = useState({});
  const [selectedPersonnel, setSelectedPersonnel] = useState('');
  const [selectedYear, setSelectedYear] = useState('all');
  const [leadData, setLeadData] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/monthly-data-sales`,
        getAuthHeader()
      );
      setData(response.data);
      
      // Fetch lead data
      try {
        const leadsRes = await axios.get(`${API_URL}/api/monthly-leads-summary`, getAuthHeader());
        setLeadData(leadsRes.data || {});
      } catch (e) {
        console.log('Lead data not available');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const togglePersonnel = (email) => {
    setExpandedPersonnel(prev => ({
      ...prev,
      [email]: !prev[email]
    }));
  };

  // Get unique years from data
  const availableYears = useMemo(() => {
    if (!data?.months) return [];
    const years = new Set();
    data.months.forEach(m => {
      const year = m.split('-')[0];
      if (year) years.add(year);
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [data?.months]);

  // Get selected personnel data
  const selectedPersonnelData = useMemo(() => {
    if (!selectedPersonnel || !data?.personnel) return null;
    return data.personnel.find(p => p.email === selectedPersonnel);
  }, [selectedPersonnel, data?.personnel]);

  // Filter monthly data by year
  const getFilteredMonthlyData = useCallback((monthlyData) => {
    if (!monthlyData) return [];
    if (selectedYear === 'all') return monthlyData;
    return monthlyData.filter(m => m.month_key.startsWith(selectedYear));
  }, [selectedYear]);

  // Calculate year totals for selected personnel
  const yearTotals = useMemo(() => {
    if (!selectedPersonnelData) return null;
    const filteredData = getFilteredMonthlyData(selectedPersonnelData.monthly_data);
    
    const totals = { total: 0, iptal_t: 0, types: {} };
    data?.sales_types?.forEach(st => { totals.types[st] = 0; });
    
    filteredData.forEach(month => {
      totals.total += month.total;
      totals.iptal_t += month.iptal_t;
      Object.entries(month.types || {}).forEach(([type, count]) => {
        totals.types[type] = (totals.types[type] || 0) + count;
      });
    });
    
    return totals;
  }, [selectedPersonnelData, selectedYear, data?.sales_types, getFilteredMonthlyData]);

  // Get lead count for selected personnel and year
  const getLeadCount = () => {
    if (!selectedPersonnel || !leadData) return 0;
    const personnelLeads = leadData[selectedPersonnel];
    if (!personnelLeads) return 0;
    if (selectedYear === 'all') {
      return Object.values(personnelLeads).reduce((sum, yearData) => {
        return sum + Object.values(yearData || {}).reduce((s, v) => s + (v || 0), 0);
      }, 0);
    }
    const yearLeads = personnelLeads[selectedYear];
    if (!yearLeads) return 0;
    return Object.values(yearLeads).reduce((s, v) => s + (v || 0), 0);
  };

  const currentLeadCount = getLeadCount();

  // Calculate grand totals across all months and personnel
  const calculateGrandTotals = () => {
    if (!data?.sales_types || !data?.monthly_totals) return null;
    
    const totals = {
      total: 0,
      iptal_t: 0,
      types: {}
    };
    
    data.sales_types.forEach(st => {
      totals.types[st] = 0;
    });

    Object.values(data.monthly_totals).forEach(monthData => {
      totals.total += monthData.total;
      totals.iptal_t += monthData.iptal_t;
      Object.entries(monthData.types).forEach(([type, count]) => {
        if (totals.types[type] !== undefined) {
          totals.types[type] += count;
        }
      });
    });

    return totals;
  };

  const grandTotals = calculateGrandTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data || !data.personnel || data.personnel.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
        <Users className="h-12 w-12 mb-4 opacity-50" />
        <p>Henüz veri bulunamadı</p>
        <Button onClick={fetchData} variant="outline" className="mt-4 gap-2">
          <RefreshCw className="h-4 w-4" />
          Yenile
        </Button>
      </div>
    );
  }

  const salesTypes = data.sales_types || [];

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="show"
      className="space-y-6"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 data-testid="mds-page-title" className="text-2xl font-bold font-[Manrope]">Aylık Data Satış</h1>
          <p className="text-muted-foreground">
            Personel ve yıl bazlı aylık dağılım ({data.personnel?.length || 0} personel, {availableYears.length} yıl)
          </p>
        </div>
        <Button data-testid="mds-refresh" onClick={fetchData} variant="outline" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Yenile
        </Button>
      </motion.div>

      {/* Filters & Controls */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* Personnel Filter */}
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Personel:</span>
                <Select value={selectedPersonnel} onValueChange={setSelectedPersonnel}>
                  <SelectTrigger data-testid="mds-personnel-filter" className="w-[250px]">
                    <SelectValue placeholder="Personel seçin..." />
                  </SelectTrigger>
                  <SelectContent>
                    {data.personnel?.map(p => (
                      <SelectItem key={p.email} value={p.email}>
                        <span>{p.name}</span>
                        <span className="ml-2 text-xs text-muted-foreground">
                          ({p.monthly_data?.reduce((s, m) => s + m.total, 0) || 0} kayıt)
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Year Filter - Only show when personnel selected */}
              {selectedPersonnel && availableYears.length > 0 && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Yıl:</span>
                  <Select value={selectedYear} onValueChange={setSelectedYear}>
                    <SelectTrigger data-testid="mds-year-filter" className="w-[120px]">
                      <SelectValue placeholder="Tümü" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tümü</SelectItem>
                      {availableYears.map(year => (
                        <SelectItem key={year} value={year}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* No Personnel Selected - Show message */}
      {!selectedPersonnel && (
        <div>
          <Card className="border-dashed">
            <CardContent className="py-12">
              <div className="flex flex-col items-center justify-center text-center text-muted-foreground">
                <UserCheck className="h-12 w-12 mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2">Personel Seçin</h3>
                <p className="text-sm max-w-md">
                  Yukarıdaki listeden bir personel seçerek yıllık performans verilerini görüntüleyebilirsiniz.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Selected Personnel Info Card */}
      {selectedPersonnelData && yearTotals && (
        <div>
          <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="h-14 w-14 rounded-full bg-primary/20 flex items-center justify-center text-lg font-bold text-primary">
                    {selectedPersonnelData.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                  </div>
                  <div>
                    <CardTitle className="text-xl">{selectedPersonnelData.name}</CardTitle>
                    <CardDescription>{selectedPersonnelData.email}</CardDescription>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-bold text-primary">
                    {selectedYear === 'all' ? 'Tüm Yıllar' : selectedYear}
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-2">
                <div className="bg-white dark:bg-slate-800 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Database className="h-4 w-4" />
                    <span className="text-xs font-medium">Toplam Kayıt</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-600">{yearTotals.total}</p>
                </div>
                
                <div className="bg-white dark:bg-slate-800 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <TrendingUp className="h-4 w-4" />
                    <span className="text-xs font-medium">Lead Sayısı</span>
                  </div>
                  <p className="text-2xl font-bold text-green-600">{currentLeadCount}</p>
                </div>
                
                <div className="bg-white dark:bg-slate-800 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <XCircle className="h-4 w-4" />
                    <span className="text-xs font-medium">İptal (T)</span>
                  </div>
                  <p className="text-2xl font-bold text-red-600">{yearTotals.iptal_t}</p>
                </div>
                
                <div className="bg-white dark:bg-slate-800 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Calendar className="h-4 w-4" />
                    <span className="text-xs font-medium">Ay Sayısı</span>
                  </div>
                  <p className="text-2xl font-bold text-purple-600">
                    {getFilteredMonthlyData(selectedPersonnelData.monthly_data).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sales Types Distribution for Selected Personnel */}
      {selectedPersonnelData && yearTotals && (
        <div>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                {selectedYear === 'all' ? 'Tüm Yıllar' : selectedYear} - P Sütunu Dağılımı
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {salesTypes.map((type, idx) => {
                  const count = yearTotals.types[type] || 0;
                  if (count === 0) return null;
                  return (
                    <div key={type} className={`px-4 py-2 rounded-lg ${getTypeColor(idx)}`} title={type}>
                      <p className="text-xs opacity-80 truncate max-w-[100px]">{type}</p>
                      <p className="text-xl font-bold">{count}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Monthly Breakdown Table for Selected Personnel */}
      {selectedPersonnelData && (
        <div>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {selectedYear === 'all' ? 'Tüm Aylar' : `${selectedYear} Yılı`} Aylık Detay
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="font-semibold sticky left-0 bg-muted/50 z-10 min-w-[140px]">
                        Ay
                      </TableHead>
                      <TableHead className="text-center font-semibold min-w-[80px] bg-blue-600 text-white">
                        Toplam
                      </TableHead>
                      <TableHead className="text-center font-semibold min-w-[80px] bg-red-600 text-white">
                        İptal (T)
                      </TableHead>
                      {salesTypes.map((type, idx) => (
                        <TableHead 
                          key={type} 
                          title={type}
                          className={`text-center font-semibold min-w-[90px] ${getTypeHeaderColor(idx)} cursor-help`}
                        >
                          {type.length > 12 ? type.slice(0, 10) + '...' : type}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getFilteredMonthlyData(selectedPersonnelData.monthly_data)?.map((monthData, rowIdx) => (
                      <TableRow 
                        key={monthData.month_key}
                        className={rowIdx % 2 === 0 ? 'bg-muted/20' : ''}
                      >
                        <TableCell className="font-medium sticky left-0 bg-inherit z-10">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-3 w-3 text-muted-foreground" />
                            {monthData.month_label}
                          </div>
                        </TableCell>
                        <TableCell className="text-center font-bold text-blue-700 dark:text-blue-300">
                          {monthData.total}
                        </TableCell>
                        <TableCell className="text-center">
                          {monthData.iptal_t > 0 ? (
                            <span className="inline-flex items-center gap-1 text-red-600 font-medium">
                              <XCircle className="h-3 w-3" />
                              {monthData.iptal_t}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        {salesTypes.map((type, idx) => {
                          const count = monthData.types?.[type] || 0;
                          return (
                            <TableCell key={type} className="text-center">
                              {count > 0 ? (
                                <span className={`inline-block px-2 py-0.5 rounded text-sm font-medium ${getTypeColor(idx)}`}>
                                  {count}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                    
                    {/* Year Total Row */}
                    {yearTotals && (
                      <TableRow className="bg-slate-100 dark:bg-slate-800 font-semibold border-t-2">
                        <TableCell className="sticky left-0 bg-slate-100 dark:bg-slate-800 z-10">
                          <strong>{selectedYear === 'all' ? 'Toplam' : `${selectedYear} Toplamı`}</strong>
                        </TableCell>
                        <TableCell className="text-center text-blue-700 dark:text-blue-300 text-lg">
                          {yearTotals.total}
                        </TableCell>
                        <TableCell className="text-center text-red-600 text-lg">
                          {yearTotals.iptal_t}
                        </TableCell>
                        {salesTypes.map((type, idx) => {
                          const total = yearTotals.types[type] || 0;
                          return (
                            <TableCell key={type} className="text-center">
                              {total > 0 ? (
                                <span className={`inline-block px-2 py-0.5 rounded text-sm font-bold ${getTypeColor(idx)}`}>
                                  {total}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Legend */}
      {salesTypes.length > 0 && selectedPersonnel && (
        <div>
          <Card className="bg-muted/30">
            <CardContent className="py-3">
              <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                <span className="font-medium">P Sütunu Değerleri:</span>
                {salesTypes.slice(0, 10).map((type, idx) => (
                  <span key={type} className={`px-2 py-1 rounded ${getTypeColor(idx)}`} title={type}>
                    {type.length > 15 ? type.slice(0, 12) + '...' : type}
                  </span>
                ))}
                {salesTypes.length > 10 && (
                  <span className="text-muted-foreground">+{salesTypes.length - 10} daha</span>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </motion.div>
  );
}
